<script setup lang="ts">
import { ErrorState, Skeleton } from '@taler/ui'
import { useI18n } from 'vue-i18n'
import InvitationsSection from '@/features/membership/components/InvitationsSection.vue'
import MembersSection from '@/features/membership/components/MembersSection.vue'
import type { Invitation, LedgerRole, Member } from '../internal/types.ts'

const props = defineProps<{
  members: readonly Member[]
  invitations: readonly Invitation[]
  isLoading: boolean
  error: Error | null
  retrying?: boolean
  isAddingMember: boolean
  addMemberError: Error | null
  changingRoleSubject: string | undefined
  changeRoleError: Error | null
  removingSubject: string | undefined
  removeError: Error | null
  isInviting: boolean
  inviteError: Error | null
  revokingId: string | undefined
  revokeError: Error | null
  locale: 'en' | 'hu'
}>()

const emit = defineEmits<{
  addMember: [input: { subject: string; role: LedgerRole }]
  changeRole: [input: { subject: string; role: LedgerRole }]
  removeMember: [subject: string]
  invite: [input: { subject: string; role: LedgerRole }]
  revoke: [invitationId: string]
  retry: []
}>()

const { t } = useI18n()
</script>

<template>
  <div class="flex flex-col gap-[var(--space-section)]">
    <ul v-if="isLoading" aria-hidden="true" class="flex flex-col gap-3">
      <li v-for="index in 4" :key="index" class="flex items-center gap-3">
        <Skeleton width="40%" height="1rem" />
        <Skeleton width="20%" height="1rem" />
      </li>
    </ul>
    <p v-if="isLoading" class="sr-only" role="status">{{ t('membership.loading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('membership.errorTitle')"
      :body="t('membership.errorBody')"
      :retry-label="t('membership.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <template v-else>
      <MembersSection
        :members="props.members"
        :is-adding="props.isAddingMember"
        :add-error="props.addMemberError"
        :changing-role-subject="props.changingRoleSubject"
        :change-role-error="props.changeRoleError"
        :removing-subject="props.removingSubject"
        :remove-error="props.removeError"
        @add="emit('addMember', $event)"
        @change-role="emit('changeRole', $event)"
        @remove="emit('removeMember', $event)"
      />

      <InvitationsSection
        :invitations="props.invitations"
        :is-inviting="props.isInviting"
        :invite-error="props.inviteError"
        :revoking-id="props.revokingId"
        :revoke-error="props.revokeError"
        :locale="props.locale"
        @invite="emit('invite', $event)"
        @revoke="emit('revoke', $event)"
      />
    </template>
  </div>
</template>
